import { EdlinkIdCode } from '@/components/alphanumeric-code';
import { EditableInfoCard } from '@/components/editable-info-card';
import { SubmitProps } from '@/components/editable-info-page';
import { SyncEdlinkDistrict } from './-components/sync-edlink-district';
import { FormComboBox } from '@/components/forms/form-combo-box';
import { FormInputDate } from '@/components/forms/form-input-date';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormSingleSelect } from '@/components/forms/form-single-select';
import { InfoKeyValue } from '@/components/info-key-value';
import { InfoPage } from '@/components/info-page';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  buildBackgroundCheckStatusSelectOption,
  buildTechCheckTypeSelectOption,
  comboBoxOptionsOf,
  multiSelectOptions,
  requiredSelectOption,
} from '@/components/ui/multi-select-utils';
import { getOrg, getOrgs, updateOrg } from '@/lib/api-calls';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling';
import { QueryKeys } from '@/lib/queries';
import { validateRouteRole } from '@/lib/routing';
import { AllTimeZones, getTimezoneTypeahead } from '@/lib/time-zones';
import { formatPlainDate, isAirAdmin } from '@airreading/common';
import {
  BackgroundCheckStatus,
  OrgFeedbackTypeLabels,
  orgFeedbackTypeSchema,
  orgStatusSchema,
  orgTypeSchema,
  StudentStatus,
  TechCheckType,
  VideoConferenceTypeLabels,
  videoConferenceTypeSchema,
} from '@airreading/common/data';
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';
import { BackgroundCheckStatusBadgeList } from '../teachers/-components/background-check-status-badge-list';
import { FormMultiSelect } from '@/components/forms/form-multi-select';
import { backgroundCheckStatusOptions, techCheckStatusOptions } from '@/lib/coach-utils';
import { TechCheckTypeBadgeList } from './-components/tech-check-type-badge-list';
import { Check, X } from 'lucide-react';
import { FormCheckbox } from '@/components/forms/form-checkbox';

export const Route = createFileRoute('/_layout/orgs/$orgId')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: OrgPage,
});

const detailsFormSchema = z.object({
  name: z.string().trim(),
  timeZone: requiredSelectOption('Select a time zone'),
  kind: orgTypeSchema,
  status: orgStatusSchema,
  edlinkId: z.string().optional(),
  videoConferenceType: videoConferenceTypeSchema.optional(),
  feedbackType: orgFeedbackTypeSchema,
  teacherBackgroundCheckStatuses: multiSelectOptions.optional(),
  teacherTechCheckTypes: multiSelectOptions.optional(),
  canSendEmailSMS: z.boolean().optional(),
  showSessionsToTeachers: z.boolean().optional(),
});

const numberOfStudentsFormSchema = z.object({
  totalSeats: z.coerce
    .number({ message: 'Please enter a valid number.' })
    .int({ message: 'Please enter a integer number.' })
    .min(0)
    .default(0)
    .optional(),
});

const preliminaryDatesFormSchema = z
  .object({
    start: z.instanceof(Temporal.PlainDate).optional(),
    end: z.instanceof(Temporal.PlainDate).optional(),
  })
  .refine(
    (data) => {
      if (data.start && data.end) {
        return Temporal.PlainDate.compare(data.start, data.end) <= 0;
      }
      return true;
    },
    {
      message: 'End date must be after start date',
      path: ['end'],
    },
  );

function OrgPage() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load district. Please try again.',
          back: {
            backLink: {
              to: '/orgs',
            },
          },
        };
      }}
    >
      <OrgDetails />
    </InfoPageSuspenseBoundary>
  );
}

function OrgDetails() {
  const { orgId } = Route.useParams();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data } = useSuspenseQuery(getOrg(orgId));

  const { mutateAsync } = useMutation({
    mutationFn: updateOrg,
  });

  const onSubmitDetails = async ({ formData, disableEditing }: SubmitProps<z.infer<typeof detailsFormSchema>>) => {
    try {
      const { teacherBackgroundCheckStatuses, teacherTechCheckTypes, ...rest } = formData;

      await mutateAsync({
        id: orgId,
        data: {
          ...rest,
          ...(teacherBackgroundCheckStatuses && {
            teacherBackgroundCheckStatuses: teacherBackgroundCheckStatuses.map(
              (item) => item.value as BackgroundCheckStatus,
            ),
          }),
          ...(teacherTechCheckTypes && {
            teacherTechCheckTypes: teacherTechCheckTypes.map((item) => item.value as TechCheckType),
          }),
          timeZone: formData.timeZone.value,
          edlinkId: ((edlinkId) => {
            switch (edlinkId) {
              case undefined: {
                return undefined;
              }
              default: {
                if (!edlinkId.trim()) {
                  return null;
                }
                return edlinkId;
              }
            }
          })(formData.edlinkId),
        },
      });
      toast({
        title: 'District updated successfully',
      });
      disableEditing();
      queryClient.invalidateQueries({ queryKey: QueryKeys.getOrg(orgId) });
      queryClient.invalidateQueries(getOrgs());
    } catch {
      toast({
        title: 'District update failed',
      });
    }
  };

  const onSubmitStudents = async (props: SubmitProps<z.infer<typeof numberOfStudentsFormSchema>>) => {
    const { formData, disableEditing } = props;

    try {
      await mutateAsync({
        id: orgId,
        data: {
          ...formData,
        },
      });
      toast({
        title: 'District updated successfully',
      });
      disableEditing();
      queryClient.invalidateQueries({ queryKey: QueryKeys.getOrg(orgId) });
      queryClient.invalidateQueries(getOrgs());
    } catch {
      toast({
        title: 'District update failed',
      });
    }
  };

  const onSubmitPreliminaryDates = async (props: SubmitProps<z.infer<typeof preliminaryDatesFormSchema>>) => {
    const { formData, disableEditing } = props;

    try {
      await mutateAsync({
        id: orgId,
        data: {
          start: formData.start?.toString(),
          end: formData.end?.toString(),
        },
      });
      toast({
        title: 'District updated successfully',
      });
      disableEditing();
      queryClient.invalidateQueries({ queryKey: QueryKeys.getOrg(orgId) });
      queryClient.invalidateQueries(getOrgs());
    } catch {
      toast({
        title: 'District update failed',
      });
    }
  };

  const studentsStatus = useMemo(() => {
    if (!data.schools) {
      return {
        enrolled: 0,
        dropped: 0,
        programConcluded: 0,
      };
    }

    return data.schools.reduce(
      (acc, cur) => {
        acc.enrolled += cur.user_roles.filter(
          (role) => role.user.studentProfile?.status === StudentStatus.enrolled,
        ).length;
        acc.dropped += cur.user_roles.filter(
          (role) => role.user.studentProfile?.status === StudentStatus.dropped,
        ).length;
        acc.programConcluded += cur.user_roles.filter(
          (role) => role.user.studentProfile?.status === StudentStatus.program_concluded,
        ).length;

        return acc;
      },
      { enrolled: 0, dropped: 0, programConcluded: 0 },
    );
  }, [data.schools]);

  return (
    <InfoPage
      headerText={data.name}
      breadcrumbs={{
        linkItems: [
          {
            label: 'Districts',
            link: {
              to: '/orgs',
            },
          },
        ],
        currentPage: data.name,
      }}
      headerActions={
        <>
          {data.edlinkId && (
            <SyncEdlinkDistrict
              orgId={data.id}
              districtName={data.name}
              districtId={data.edlinkId}
              trigger={<Button>Sync from Edlink</Button>}
            />
          )}
        </>
      }
    >
      <EditableInfoCard
        headerText="District details"
        formSchema={detailsFormSchema}
        onSubmit={onSubmitDetails}
        defaultValues={{
          name: data.name,
          kind: data.kind || 'district',
          timeZone: {
            value: data.timeZone || '',
            label: AllTimeZones.find((tz) => tz.value === data.timeZone)?.label || '',
          },
          status: data.status,
          edlinkId: data.edlinkId || undefined,
          videoConferenceType: data.videoConferenceType,
          feedbackType: data.feedbackType,
          teacherBackgroundCheckStatuses: data?.teacherBackgroundCheckStatuses?.map(
            buildBackgroundCheckStatusSelectOption,
          ),
          teacherTechCheckTypes: data?.teacherTechCheckTypes?.map(buildTechCheckTypeSelectOption),
          canSendEmailSMS: data?.canSendEmailSMS,
          showSessionsToTeachers: data?.showSessionsToTeachers,
        }}
      >
        {({ form, editing }) => {
          return (
            <>
              <InfoKeyValue
                label="District Name"
                value={data.name}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="name"
                      label="District name"
                      inputProps={{ placeholder: 'District name' }}
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Time zone"
                value={data.timeZone}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormComboBox
                      name="timeZone"
                      label="Time zone"
                      form={form}
                      {...comboBoxOptionsOf(getTimezoneTypeahead)}
                      triggerProps={{ className: 'w-2/3' }}
                      required
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Feedback Type"
                value={OrgFeedbackTypeLabels[data.feedbackType]}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormSingleSelect
                      name="feedbackType"
                      label="Feedback Type"
                      className="w-2/3"
                      form={form}
                      options={orgFeedbackTypeSchema.options}
                      renderOption={(type) => (type ? OrgFeedbackTypeLabels[type] : type)}
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Meeting Type"
                value={VideoConferenceTypeLabels[data.videoConferenceType]}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormSingleSelect
                      name="videoConferenceType"
                      label="Meeting Type"
                      className="w-2/3"
                      form={form}
                      options={videoConferenceTypeSchema.options}
                      renderOption={(type) => (type ? VideoConferenceTypeLabels[type] : type)}
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Edlink ID"
                value={!editing && !data.edlinkId ? '-' : <EdlinkIdCode code={data.edlinkId} />}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="edlinkId"
                      label="Edlink ID"
                      inputProps={{ className: 'font-mono', placeholder: '-' }}
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Teacher background check"
                value={
                  data?.teacherBackgroundCheckStatuses?.length ? (
                    <BackgroundCheckStatusBadgeList statuses={data.teacherBackgroundCheckStatuses} />
                  ) : (
                    '-'
                  )
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormMultiSelect
                      form={form}
                      name="teacherBackgroundCheckStatuses"
                      label="Teacher background check"
                      multipleSelectorProps={{
                        options: backgroundCheckStatusOptions,
                        placeholder: 'Select teacher background check statuses...',
                      }}
                      className="w-2/3"
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Teacher tech check"
                value={
                  data?.teacherTechCheckTypes?.length ? (
                    <TechCheckTypeBadgeList techs={data.teacherTechCheckTypes} />
                  ) : (
                    '-'
                  )
                }
                edit={{
                  editing,
                  editModeComponent: (
                    <FormMultiSelect
                      form={form}
                      name="teacherTechCheckTypes"
                      label="Teacher tech check"
                      multipleSelectorProps={{
                        options: techCheckStatusOptions,
                        placeholder: 'Select teacher tech checks...',
                      }}
                      className="w-2/3"
                    />
                  ),
                }}
              />

              <InfoKeyValue
                label="Can send Email/SMS"
                value={data?.canSendEmailSMS ? <Check /> : <X />}
                edit={{
                  editing,
                  editModeComponent: <FormCheckbox name="canSendEmailSMS" label="Can send Email/SMS" form={form} />,
                }}
              />

              <InfoKeyValue
                label="Show sessions to teachers"
                value={data?.showSessionsToTeachers ? <Check /> : <X />}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormCheckbox name="showSessionsToTeachers" label="Show sessions to teachers" form={form} />
                  ),
                }}
              />
            </>
          );
        }}
      </EditableInfoCard>
      <EditableInfoCard
        headerText="Number of Students"
        formSchema={numberOfStudentsFormSchema}
        onSubmit={onSubmitStudents}
        defaultValues={{ totalSeats: data.totalSeats }}
      >
        {({ form, editing }) => {
          return (
            <div className="grid grid-cols-5">
              <InfoKeyValue
                label="Total Seats"
                value={data.totalSeats}
                edit={{
                  editing,
                  editModeComponent: (
                    <FormInputField
                      form={form}
                      name="totalSeats"
                      label="Total Seats"
                      inputProps={{ placeholder: 'Total Seats', type: 'number' }}
                    />
                  ),
                }}
              />

              <InfoKeyValue label="Enrolled" value={studentsStatus.enrolled} />
              <InfoKeyValue label="Dropped" value={studentsStatus.dropped} />
              <InfoKeyValue label="Program concluded" value={studentsStatus.programConcluded} />
            </div>
          );
        }}
      </EditableInfoCard>
      <EditableInfoCard
        headerText="Preliminary Dates"
        formSchema={preliminaryDatesFormSchema}
        onSubmit={onSubmitPreliminaryDates}
        defaultValues={{
          start: data.preliminaryStartPlainDate ?? undefined,
          end: data.preliminaryEndPlainDate ?? undefined,
        }}
      >
        {({ form, editing }) => {
          return (
            <>
              <InfoKeyValue
                label="Preliminary start date"
                value={data.preliminaryStartPlainDate ? formatPlainDate(data.preliminaryStartPlainDate) : undefined}
                edit={{
                  editing,
                  editModeComponent: <FormInputDate form={form} name="start" label="Preliminary start date" />,
                }}
              />
              <InfoKeyValue
                label="Preliminary end date"
                value={data.preliminaryEndPlainDate ? formatPlainDate(data.preliminaryEndPlainDate) : undefined}
                edit={{
                  editing,
                  editModeComponent: <FormInputDate form={form} name="end" label="Preliminary end date" />,
                }}
              />
            </>
          );
        }}
      </EditableInfoCard>
    </InfoPage>
  );
}
